import React, { useState, useCallback, useContext } from 'react';
import { Row, Col, OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import '@ag-grid-community/core/dist/styles/ag-grid.css';
import '@ag-grid-community/core/dist/styles/ag-theme-alpine.css';
import MassiveUploadProject from 'components/form/upload/massiveUploadProject';
import { columnCentered, columnToRight } from '@lib/helpers/agGridHelpers';
import { dateFormatter, formatToDecimals } from '@lib/helpers/formatting';
import { months } from '@lib/helpers/calendar';
import IndustrialAccounts from 'components/atomic/organisms/IndustrialAccounts/IndustrialAccounts';
import { UserContext } from 'components/contexts/UserProvider';
import NavTabs from '@components/atomic/molecules/NavTabs/NavTabs';
import ExportTemplateModal from './ExportTemplateModal';
import SummaryInvoiceModal from './SummaryInvoiceModal';
import ExecutionChecklistCellRenderer from './ExecutionChecklistCellRenderer';
import SummaryContractModal from './SummaryContractModal';
import SummaryPaymentModal from './SummaryPaymentModal';
import { ConfigureTemplate } from '@icons';
import GenerateTrackRecord from '@components/generateTrackRecord';
import SummaryRecordModal from './SummaryRecordModal';
import { useParams } from 'react-router-dom';

const Execution = ({ project }) => {
	const { projectId } = useParams();
	const { isOe, userAccess, isPartnerUser } = useContext(UserContext);
	const [isTemplateModalOpen, setTemplateModalOpen] = useState(false);
	const [errorsContracts, setErrorsContracts] = useState([]);
	const [errorsInvoices, setErrorsInvoices] = useState([]);
	const [errorsPayments, setErrorsPayments] = useState([]);
	const [errorsTimesheets, setErrorsTimesheets] = useState([]);
	const [refresh, setRefresh] = useState(false);
	const isProjectWp = project?.streamConfigurationType === 'WP';
	const canGenerateTemplates =
		project?.advancedConfig && project?.advancedConfig.some((el) => el.name === 'can_generate_templates' && el.enabled);

	const isValidJSON = (jsonString) => {
		try {
			JSON.parse(jsonString);
			return true;
		} catch (error) {
			return false;
		}
	};

	const CustomTooltipRenderer = (params) => {
		let message = '';

		if (params.type === 'invoices') {
			message = 'Fatture';
		} else if (params.type === 'invoicePayment') {
			message = 'Pagamenti';
		} else if (params.type === 'invoiceReceipt') {
			message = 'Quietanze';
		}

		return (
			<div className="tooltipInfoBox" style={{ width: 'auto' }}>
				<span>{message}</span>
			</div>
		);
	};

	const logsCellRenderer = (params) => {
		if (params.value) {
			const isValid = isValidJSON(params.value);
			if (isValid) {
				const JSONerrors = JSON.parse(params.value);
				const arrayErrors = Object.values(JSONerrors);
				return arrayErrors.map((error, index) => (
					<p key={index} style={{ color: 'red' }}>
						{Object.values(error)[0]}
					</p>
				));
			} else {
				return <span>{params.value}</span>;
			}
		} else {
			return null;
		}
	};

	const makeSemaphores = (data) => {
		const stoplightColor = {
			red: '#C94723',
			yellow: '#D4BE31',
			green: '#689689',
			white: '#ffffff',
			gray: '#C4C4C4',
			grey: '#C4C4C4',
			green_white: '#689689',
		};

		let circleStyle;
		let message = '';

		if (data) {
			if (data.color === 'green_white' || data.color === 'green_white_settled') {
				circleStyle = {
					display: 'inherit',
					width: '23px',
					height: '23px',
					border: '4px solid #689689',
					borderRadius: '50%',
					padding: '0px',
					background: data.color === 'green_white_settled' ? 'linear-gradient(to right, #689689 50%, transparent 0)' : 'white',
					boxSizing: 'border-box',
				};

				message = data.color === 'green_white_settled' ? 'Conforme - validata' : 'Conforme - da validare';
			} else {
				circleStyle = {
					display: 'inherit',
					width: '1.2em',
					height: '1.2em',
					borderRadius: '2em',
					MozBorderRadius: '2em',
					WebkitBorderRadius: '2em',
					backgroundColor: stoplightColor[data.color],
					border: `2px solid ${stoplightColor[data.color]}`,
				};

				switch (data.color) {
					case 'white':
						message = 'Da lavorare';
						break;
					case 'grey':
					case 'gray':
						message = 'In lavorazione';
						break;
					case 'red':
						message = 'Non conforme';
						break;
					case 'yellow':
						message = 'Incompleta';
						break;
					case 'green':
						message = 'Conforme';
						break;
					default:
						break;
				}
			}
		} else {
			circleStyle = {
				display: 'inherit',
				width: '1.2em',
				height: '1.2em',
				borderRadius: '2em',
				MozBorderRadius: '2em',
				WebkitBorderRadius: '2em',
				backgroundColor: '#ffffff',
				border: `2px solid ${'#C4C4C4'}`,
			};

			message = 'Da lavorare';
		}

		return (
			<Row className="align-items-center justify-content-center h-100">
				<Col className="col-auto">
					<OverlayTrigger placement="top" overlay={<Tooltip>{message}</Tooltip>}>
						<i style={{ ...circleStyle }} />
					</OverlayTrigger>
				</Col>
			</Row>
		);
	};

	const ChecklistCellRenderer = useCallback((params) => {
		if (params.type === 'invoicePayment') {
			return makeSemaphores(params.data.invoicePayment?.checkliststatus);
		} else if (params.type === 'invoiceReceipt') {
			return makeSemaphores(params.data.invoiceReceipt?.checkliststatus);
		} else {
			return makeSemaphores(params.value);
		}
	}, []);

	const LogCellRenderer = useCallback((params) => {
		const red = '#C94723';
		// const yellow = '#D4BE31';
		const green = '#689689';

		const legendStyles = {
			bullet: {
				display: 'inherit',
				width: '1.2em',
				height: '1.2em',
				borderRadius: '2em',
			},
			label: {
				display: 'inline-block',
				width: '50%',
				lineHeight: '2.5em',
			},
		};

		if (params.value) {
			return (
				<Row className="align-items-center justify-content-center h-100">
					<Col className="col-auto">
						<i
							style={{
								...legendStyles.bullet,
								backgroundColor: params.value === 'OK' ? green : red,
								textAlign: 'left',
							}}
						/>
					</Col>
				</Row>
			);
		}
	}, []);

	const contractColumn = [
		...(isProjectWp && isPartnerUser
			? [
					{
						headerName: 'Beneficiario',
						field: 'companyOwner.name',
						filter: 'agTextColumnFilter',
					},
				]
			: []),
		{
			headerName: 'N. documento',
			field: 'number',
			filter: 'agTextColumnFilter',
		},
		/* {
          headerName: 'N. Contratto Quadro',
          field: '',
          filter: 'agTextColumnFilter',
       }, */
		{
			headerName: 'Data',
			field: 'date',
			type: 'rightAligned',
			// cellRenderer: poCellRenderer,
			cellRenderer: (params) => {
				return dateFormatter(params.value);
			},
			//filter: 'agTextColumnFilter',
			...columnCentered(),
			//minWidth: 100,
		},

		{
			headerName: 'Codice Fornitore',
			field: 'supplierCode',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Fornitore',
			field: 'supplierName',
			filter: 'agTextColumnFilter',
		},
		// {
		//   headerName: 'Importo €',
		//   type: 'rightAligned',
		//   field: 'baseAmount',
		//   valueGetter: (params) => {
		//     return formatToDecimals(params.data.baseAmount);
		//   },
		//   filter: 'agTextColumnFilter',
		// },

		{
			headerName: '',
			field: 'checkliststatus',
			filter: 'agTextColumnFilter',
			cellRenderer: ChecklistCellRenderer,
			maxWidth: 70,
			cellStyle: { display: 'flex', justifyContent: 'center' },
			pinned: 'right',
		},
		{
			headerName: '',
			pinned: 'right',
			minWidth: 70,
			maxWidth: 70,
			cellStyle: { textAlign: 'center', padding: 0, justifyContent: 'center' },
			field: 'href',
			cellRenderer: ExecutionChecklistCellRenderer,
			cellRendererParams: {
				modalComponent: SummaryContractModal,
				project,
				// setRefresh: setRefresh,
				// projectAdvancedConfig: project.advancedConfig,
				//contracts: contracts,
			},
			// cellRenderer: ProjectContractModal,
			// cellRendererParams: {
			// 	setRefresh: setRefresh,
			// 	projectStreamConfigurationType: project.streamConfigurationType,
			// },
		},
	];
	const contractStageColumn = [
		{
			headerName: project.cupType === 'I' ? 'ID Progetto' : 'CUP',
			field: 'projectCup',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'CIG',
			field: 'tenderCig',
			filter: 'agTextColumnFilter',
			valueGetter: (params) => {
				const value = params.data.tenderCig.startsWith('AFFIDAMENTO_' || 'SPESEPERSONALE_')
					? 'N.A.'
					: params.data.tenderCig.startsWith('CIGTEMP_' || 'CIGTSTEMP_ ')
						? 'N.D.'
						: params.data.tenderCig;
				return value;
			},
		},
		{
			headerName: 'Messaggi',
			field: 'log',
			cellRenderer: logsCellRenderer,
			filter: 'agTextColumnFilter',
			minWidth: 460,
		},
		{
			headerName: 'NR. contratto',
			field: 'contractNumber',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Inizio contratto',
			field: 'date',
			cellRenderer: (params) => {
				return dateFormatter(params.value);
			},
			//filter: 'agTextColumnFilter',
			...columnCentered('fst-italic'),
		},
		{
			headerName: 'Nome fornitore',
			field: 'supplierName',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Importo base €',
			field: 'baseAmount',
			filter: 'agTextColumnFilter',
			//maxWidth: 170,
			valueGetter: (params) => {
				return formatToDecimals(params.data.baseAmount);
			},
			...columnToRight('fst-italic'),
		},
		{
			headerName: '',
			field: 'status',
			cellRenderer: LogCellRenderer,
			// filter: 'agTextColumnFilter',
			cellStyle: { display: 'flex', justifyContent: 'center' },
			maxWidth: 70,
			minWidth: 70,
			pinned: 'right',
		},
	];
	const contractStageClColumn = [
		{
			headerName: 'Nr. Contratto',
			field: 'entityNumber',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Messaggi',
			field: 'log',
			cellRenderer: logsCellRenderer,
			filter: 'agTextColumnFilter',
			//minWidth: 460,
			resizable: true,
		},
		{
			headerName: 'Nr. Risposte',
			field: 'answeredQuestion',
			filter: 'agTextColumnFilter',
			// type: 'rightAligned',
			...columnCentered('fst-italic'),
			//minWidth: 250,
		},
		{
			headerName: 'Nr. Domande',
			field: 'numberOfQuestions',
			filter: 'agTextColumnFilter',
			// type: 'rightAligned',
			...columnCentered('fst-italic'),
		},
		{
			headerName: '',
			field: 'status',
			cellRenderer: LogCellRenderer,
			// filter: 'agTextColumnFilter',
			cellStyle: { display: 'flex', justifyContent: 'center' },
			maxWidth: 60,
			minWidth: 60,
			pinned: 'right',
		},
	];

	const invoicesColumn = [
		...(isProjectWp && isPartnerUser
			? [
					{
						headerName: 'Beneficiario',
						field: 'companyOwner.name',
						filter: 'agTextColumnFilter',
					},
				]
			: []),
		{
			headerName: 'Nr. Fattura',
			field: 'number',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'ID Fattura',
			field: 'rpCode',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Data',
			field: 'date',
			cellRenderer: (params) => {
				return dateFormatter(params.value);
			},
			//filter: 'agTextColumnFilter',
			...columnCentered(),
			maxWidth: 150,
		},

		{
			headerName: 'Codice Fornitore',
			field: 'supplierCode',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Nome fornitore',
			field: 'supplierName',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Totale imponibile €',
			field: 'taxableAmount',
			valueGetter: (params) => {
				return formatToDecimals(params.data.taxableAmount);
			},
			filter: 'agTextColumnFilter',
			type: 'rightAligned',
		},
		{
			headerName: 'F',
			field: 'checkliststatus',
			cellRenderer: ChecklistCellRenderer,
			maxWidth: 70,
			pinned: 'right',
			tooltipComponent: CustomTooltipRenderer,
			tooltipField: 'checkliststatus', //obbligatorio per custom tooltip
			tooltipComponentParams: { type: 'invoices' },
			...columnCentered('', { textAlign: 'center', alignItems: 'center' }),
		},
		{
			headerName: 'P',
			field: 'invoicePayment',
			cellRenderer: ChecklistCellRenderer,
			cellRendererParams: { type: 'invoicePayment' },
			maxWidth: 70,
			pinned: 'right',
			tooltipComponent: CustomTooltipRenderer,
			tooltipField: 'checkliststatus', //obbligatorio per custom tooltip
			tooltipComponentParams: { type: 'invoicePayment' },
			...columnCentered('', { textAlign: 'center', alignItems: 'center' }),
		},
		/* {
      //RIATTIVARE QUANDO SARANNO ATTIVE LE QUIETANZE
      headerName: '',
      field: 'invoiceReceipt',
      cellRenderer: ChecklistCellRenderer,
      cellRendererParams: { type: 'invoiceReceipt' },
      maxWidth: 70,
      pinned: 'right',
      tooltipComponent: CustomTooltipRenderer,
      tooltipField: 'checkliststatus', //obbligatorio per custom tooltip
      tooltipComponentParams: { type: 'invoiceReceipt' },
      ...columnCentered('', { textAlign: 'center', alignItems: 'center' }),
    }, */
		{
			headerName: '',
			pinned: 'right',
			minWidth: 70,
			maxWidth: 70,
			cellStyle: { textAlign: 'center', padding: 0 },
			// cellRenderer: ProjectInvoiceModal,
			field: 'href',
			cellRenderer: ExecutionChecklistCellRenderer,
			cellRendererParams: {
				modalComponent: SummaryInvoiceModal,
				project,
				// setRefresh: setRefresh,
				// projectAdvancedConfig: project.advancedConfig,
				//contracts: contracts,
			},
		},
	];
	const invoicesStageColumn = [
		{
			headerName: '',
			field: 'status',
			cellRenderer: LogCellRenderer,
			cellStyle: { display: 'flex', justifyContent: 'center' },
			// filter: 'agTextColumnFilter',
			maxWidth: 60,
			minWidth: 60,
		},
		{
			headerName: project.cupType === 'I' ? 'ID Progetto' : 'CUP',
			field: 'projectCup',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'ID Fattura',
			field: 'rpCode',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Stream',
			field: 'projectStreamName',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Sottovoce di spesa',
			field: 'projectExpenseItem',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},

		{
			headerName: 'Messaggi',
			field: 'log',
			cellRenderer: logsCellRenderer,
			filter: 'agTextColumnFilter',
			minWidth: 460,
		},
		{
			headerName: 'CIG',
			field: 'tenderCig',
			filter: 'agTextColumnFilter',
			minWidth: 150,
			valueGetter: (params) => {
				const value = params.data.tenderCig.startsWith('AFFIDAMENTO_' || 'SPESEPERSONALE_')
					? 'N.A.'
					: params.data.tenderCig.startsWith('CIGTEMP_' || 'CIGTSTEMP_ ')
						? 'N.D.'
						: params.data.tenderCig;
				return value;
			},
		},
		{
			headerName: 'Nr. Fattura',
			field: 'number',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Data',
			field: 'date',
			cellRenderer: (params) => {
				return dateFormatter(params.value);
			},
			//filter: 'agTextColumnFilter',
			minWidth: 150,
			...columnCentered('fst-italic'),
		},
		{
			headerName: 'Fattispecie RSI',
			field: 'rdiTypeCode',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Elettronica',
			field: 'electronic',
			cellRenderer: (params) => {
				return params.value ? 'Si' : 'No';
			},
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Nome fornitore',
			field: 'supplierName',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: '% IVA',
			field: 'vatPercentage',
			filter: 'agTextColumnFilter',
			//type: 'rightAligned',
			minWidth: 150,
			...columnToRight('fst-italic'),
		},
		{
			headerName: 'Totale imponibile €',
			field: 'taxableAmount',
			filter: 'agTextColumnFilter',
			//type: 'rightAligned',
			minWidth: 150,
			valueGetter: (params) => {
				return formatToDecimals(params.data.taxableAmount);
			},
			...columnToRight('fst-italic'),
		},
		{
			headerName: 'Totale fattura €',
			field: 'totalAmount',
			filter: 'agTextColumnFilter',
			//type: 'rightAligned',
			minWidth: 150,
			valueGetter: (params) => {
				return formatToDecimals(params.data.totalAmount);
			},
			...columnToRight('fst-italic'),
		},
	];
	const invoicesStageClColumn = [
		{
			headerName: '',
			field: 'status',
			cellRenderer: LogCellRenderer,
			cellStyle: { display: 'flex', justifyContent: 'center' },
			// filter: 'agTextColumnFilter',
			maxWidth: 60,
			minWidth: 60,
		},
		{
			headerName: 'Nr. Fattura',
			field: 'entityNumber',
			filter: 'agTextColumnFilter',
		},
		//  {
		//    headerName: 'Stato',
		//    field: 'status',
		//    filter: 'agTextColumnFilter',
		//  },

		{
			headerName: 'Messaggi',
			field: 'log',
			cellRenderer: logsCellRenderer,
			filter: 'agTextColumnFilter',
			//minWidth: 460,
			resizable: true,
		},
		{
			headerName: 'Nr. Risposte',
			field: 'answeredQuestion',
			filter: 'agTextColumnFilter',
			// type: 'rightAligned',
			...columnCentered('fst-italic'),
			//minWidth: 250,
		},
		{
			headerName: 'Nr. Domande',
			field: 'numberOfQuestions',
			filter: 'agTextColumnFilter',
			// type: 'rightAligned',
			...columnCentered('fst-italic'),
		},
	];

	const timesheetsColumn = [
		...(isProjectWp && isPartnerUser
			? [
					{
						headerName: 'Beneficiario',
						field: 'companyOwner.name',
						filter: 'agTextColumnFilter',
					},
				]
			: []),
		{
			headerName: 'Nr. Record',
			field: 'number',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Anno',
			field: 'year',
			valueGetter: (params) => {
				return params?.data?.details?.[0]?.year;
			},
			//filter: 'agTextColumnFilter',
			minWidth: 150,
			...columnCentered(),
		},
		{
			headerName: 'Mese',
			field: 'month',
			valueGetter: (params) => {
				return months()[params?.data?.details?.[0]?.month - 1];
			},
			//filter: 'agTextColumnFilter',
			minWidth: 150,
			...columnCentered(),
		},
		{
			headerName: 'Matricola',
			field: 'serialNumber',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Costo sul Progetto €',
			field: 'projectCost',
			valueGetter: (params) => {
				return formatToDecimals(
					params?.data?.details?.reduce((acc, curr) => {
						return acc + curr.projectCost;
					}, 0),
				);
			},
			type: 'rightAligned',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},

		{
			headerName: '',
			field: 'checkliststatus',
			filter: 'agTextColumnFilter',
			cellRenderer: ChecklistCellRenderer,
			maxWidth: 70,
			cellStyle: { display: 'flex', justifyContent: 'center' },
			pinned: 'right',
		},
		{
			headerName: '',
			pinned: 'right',
			minWidth: 90,
			maxWidth: 90,
			field: 'href',
			cellStyle: { textAlign: 'center', padding: 0 },
			cellRenderer: ExecutionChecklistCellRenderer,
			cellRendererParams: {
				project,
				modalComponent: SummaryRecordModal,
			},
		},
	];
	const timesheetsStageColumn = [
		{
			headerName: '',
			field: 'status',
			cellRenderer: LogCellRenderer,
			// filter: 'agTextColumnFilter',
			maxWidth: 60,
			minWidth: 60,
		},
		{
			headerName: project.cupType === 'I' ? 'ID Progetto' : 'CUP',
			field: 'projectCup',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Stream',
			field: 'projectStreamName',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Voce di spesa',
			field: 'projectExpenseItem',
			filter: 'agTextColumnFilter',
			minWidth: 170,
		},

		{
			headerName: 'Messaggi',
			field: 'log',
			cellRenderer: logsCellRenderer,
			filter: 'agTextColumnFilter',
			minWidth: 460,
		},
		{
			headerName: 'CIG',
			field: 'tenderCig',
			filter: 'agTextColumnFilter',
			minWidth: 150,
			valueGetter: (params) => {
				const value = params.data.tenderCig.startsWith('AFFIDAMENTO_' || 'SPESEPERSONALE_')
					? 'N.A.'
					: params.data.tenderCig.startsWith('CIGTEMP_' || 'CIGTSTEMP_ ')
						? 'N.D.'
						: params.data.tenderCig;
				return value;
			},
		},
		{
			headerName: 'Nr. Record',
			field: 'number',
			filter: 'agTextColumnFilter',
			minWidth: 170,
		},
		{
			headerName: 'Anno',
			field: 'year',
			//filter: 'agTextColumnFilter',
			minWidth: 130,
			...columnCentered('fst-italic'),
		},
		{
			headerName: 'Mese',
			field: 'month',
			cellRenderer: (params) => {
				return months()[params?.value - 1];
			},
			//filter: 'agTextColumnFilter',
			minWidth: 150,
			...columnCentered('fst-italic'),
		},
		{
			headerName: 'Giorno',
			field: 'day',
			/* cellRenderer: (params) => {
        return months()[params?.value - 1];
      }, */
			//filter: 'agTextColumnFilter',
			minWidth: 130,
			...columnCentered('fst-italic'),
		},
		{
			headerName: 'Ore lavorate',
			field: 'workHours',
			//filter: 'agTextColumnFilter',
			maxWidth: 160,
			minWidth: 160,
			...columnCentered('fst-italic'),
		},
		{
			headerName: 'Matricola',
			field: 'serialNumber',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Nominativo',
			field: 'name',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Categoria',
			field: 'category',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Costo orario €',
			field: 'hourlyRate',
			filter: 'agTextColumnFilter',
			valueGetter: (params) => {
				return formatToDecimals(params.data.hourlyRate);
			},
			//type: 'rightAligned',
			minWidth: 170,
			...columnToRight('fst-italic'),
		},
		{
			headerName: 'Ore',
			field: 'projectHours',
			//filter: 'agTextColumnFilter',
			maxWidth: 80,
			minWidth: 80,
			...columnCentered('fst-italic'),
		},
		{
			headerName: 'Costo sul progetto €',
			field: 'projectCost',
			filter: 'agTextColumnFilter',
			valueGetter: (params) => {
				return formatToDecimals(params.data.projectCost);
			},
			//type: 'rightAligned',
			minWidth: 220,
			...columnToRight('fst-italic'),
		},
		{
			headerName: "Lettera d'assegnazione",
			field: 'assignmentLetter',
			filter: 'agTextColumnFilter',
			minWidth: 250,
		},
		{
			headerName: 'Record',
			field: 'timesheetDocument',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Cedolino',
			field: 'payslipDocument',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
	];
	const timesheetsStageClColumn = [
		{
			headerName: '',
			field: 'status',
			cellRenderer: LogCellRenderer,
			// filter: 'agTextColumnFilter',
			maxWidth: 60,
			minWidth: 60,
		},
		{
			headerName: 'Nr. Record',
			field: 'entityNumber',
			filter: 'agTextColumnFilter',
		},
		//  {
		//    headerName: 'Stato',
		//    field: 'status',
		//    filter: 'agTextColumnFilter',
		//  },
		{
			headerName: 'Messaggi',
			field: 'log',
			cellRenderer: logsCellRenderer,
			filter: 'agTextColumnFilter',
			//minWidth: 460,
			resizable: true,
		},
		{
			headerName: 'Nr. Risposte',
			field: 'answeredQuestion',
			filter: 'agTextColumnFilter',
			...columnCentered('fst-italic'),
			//minWidth: 250,
		},
		{
			headerName: 'Nr. Domande',
			field: 'numberOfQuestions',
			filter: 'agTextColumnFilter',
			...columnCentered('fst-italic'),
		},
	];

	const paymentsColumn = [
		...(isProjectWp && isPartnerUser
			? [
					{
						headerName: 'Beneficiario',
						field: 'companyOwner.name',
						filter: 'agTextColumnFilter',
					},
				]
			: []),
		{
			headerName: 'ID Pagamento',
			field: 'paymentNumber',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Data',
			field: 'date',
			cellRenderer: (params) => {
				return dateFormatter(params.value);
			},
			//filter: 'agTextColumnFilter',
			minWidth: 150,
			...columnCentered(),
		},
		{
			headerName: 'Codice Fornitore',
			field: 'payeeCode',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Nome Fornitore',
			field: 'payeeName',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		// {
		//   headerName: 'Importo €',
		//   field: 'invoiceAmount',
		//   valueGetter: (params) => {
		//     return formatToDecimals(params.data.invoiceAmount);
		//   },
		//   type: 'rightAligned',
		//   //...columnToRight(),
		//   filter: 'agTextColumnFilter',
		//   minWidth: 150,
		// },
		{
			headerName: '',
			pinned: 'right',
			minWidth: 90,
			maxWidth: 90,
			cellStyle: { textAlign: 'center', padding: 0 },
			field: 'href',
			cellRenderer: ExecutionChecklistCellRenderer,
			cellRendererParams: {
				modalComponent: SummaryPaymentModal,
				project,
			},
			// cellRenderer: ProjectPaymentModal,
			// cellRendererParams: {
			// 	setRefresh: setRefresh,
			// },
		},
	];
	const paymentsStageColumn = [
		{
			headerName: '',
			field: 'status',
			cellRenderer: LogCellRenderer,
			// filter: 'agTextColumnFilter',
			maxWidth: 60,
			minWidth: 60,
		},
		{
			headerName: 'ID Pagamento',
			field: 'paymentNumber',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Messaggi',
			field: 'log',
			cellRenderer: logsCellRenderer,
			filter: 'agTextColumnFilter',
			minWidth: 460,
		},
		{
			headerName: 'Data',
			field: 'date',
			cellRenderer: (params) => {
				return dateFormatter(params.value);
			},
			//filter: 'agTextColumnFilter',
			minWidth: 150,
			...columnCentered('fst-italic'),
		},
		{
			headerName: 'Codice Fornitore',
			field: 'payeeCode',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Nome Fornitore',
			field: 'payeeName',
			filter: 'agTextColumnFilter',
			minWidth: 150,
		},
		{
			headerName: 'Importo €',
			field: 'invoiceAmount',
			valueGetter: (params) => {
				return formatToDecimals(params.data.invoiceAmount);
			},
			//type: 'rightAligned',
			filter: 'agTextColumnFilter',
			minWidth: 150,
			...columnToRight('fst-italic'),
		},
	];
	const paymentsStageClColumn = [
		{
			headerName: 'Nr. Fattura',
			field: 'entityNumber',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Stato',
			field: 'status',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Errori',
			field: 'log',
			cellRenderer: logsCellRenderer,
			filter: 'agTextColumnFilter',
			//minWidth: 460,
			resizable: true,
		},
		{
			headerName: 'Nr. Risposte',
			field: 'answeredQuestion',
			filter: 'agTextColumnFilter',
			...columnCentered('fst-italic'),
			//minWidth: 250,
		},
		{
			headerName: 'Nr. Domande',
			field: 'numberOfQuestions',
			filter: 'agTextColumnFilter',
			...columnCentered('fst-italic'),
		},
	];
	// if (!isOe || !userAccess.isExternalAdmin) return <Unauthorized />;

	const tabNavItems = [
		// {
		// 	eventKey: 'configuration-template',
		// 	title: 'Configurazione File Template',
		// 	transCode: 'common.execution.configuration_template',
		// 	shouldBeVisible:
		// 		(isOe || userAccess.isExternalAdmin || (isCoordinator && isProjectWp)) && canGenerateTemplates,
		// 	content: (
		// 		<GenerateTemplatesSection projectCompanyId={project.companyId} projectId={projectId} />
		// 	),
		// },
		{
			eventKey: 'contracts',
			title: project.streamConfigurationType === 'WP' ? 'Contratto/subappalto' : 'Incarichi / attingimenti',
			transCode: 'project.execution.contracts_subcontracts',
			shouldBeVisible: isOe || userAccess.isExternalAdmin || (isProjectWp && isPartnerUser),
			content: (
				<MassiveUploadProject
					column={contractColumn}
					columnStage={contractStageColumn}
					columnStageCl={contractStageClColumn}
					entityType="contracts"
					projectId={projectId}
					errors={errorsContracts}
					setErrors={setErrorsContracts}
					projectName={project.name}
					projectCup={project.cup}
					isProjectWp={isProjectWp}
					hasEnabledAi={project.aiEnabled}
					key="contracts-key"
					refresh={refresh}
					setRefresh={setRefresh}
				/>
			),
		},
		{
			eventKey: 'invoices',
			title: 'Fatture',
			transCode: 'project.execution.invoices',
			shouldBeVisible: isOe || userAccess.isExternalAdmin || (isProjectWp && isPartnerUser),
			content: (
				<MassiveUploadProject
					column={invoicesColumn}
					columnStage={invoicesStageColumn}
					columnStageCl={invoicesStageClColumn}
					entityType="invoices"
					projectId={projectId}
					errors={errorsInvoices}
					setErrors={setErrorsInvoices}
					projectName={project.name}
					projectCup={project.cup}
					isProjectWp={isProjectWp}
					hasEnabledAi={project.aiEnabled}
					key="invoices-key"
					refresh={refresh}
					setRefresh={setRefresh}
				/>
			),
		},
		{
			eventKey: 'payments',
			title: 'Pagamenti',
			transCode: 'project.execution.payments',
			shouldBeVisible: isOe || userAccess.isExternalAdmin || (isProjectWp && isPartnerUser),
			content: (
				<MassiveUploadProject
					column={paymentsColumn}
					columnStage={paymentsStageColumn}
					columnStageCl={paymentsStageClColumn}
					entityType="payments"
					projectId={projectId}
					errors={errorsPayments}
					setErrors={setErrorsPayments}
					projectName={project.name}
					projectCup={project.cup}
					isProjectWp={isProjectWp}
					hasEnabledAi={project.aiEnabled}
					key="payments-key"
					refresh={refresh}
					setRefresh={setRefresh}
				/>
			),
		},
		{
			eventKey: 'timesheets',
			title: 'Personale',
			transCode: 'project.execution.timesheets',
			shouldBeVisible: isOe || userAccess.isExternalAdmin || (isProjectWp && isPartnerUser),
			content: (
				<MassiveUploadProject
					column={timesheetsColumn}
					columnStage={timesheetsStageColumn}
					columnStageCl={timesheetsStageClColumn}
					entityType="timesheets"
					projectId={projectId}
					errors={errorsTimesheets}
					setErrors={setErrorsTimesheets}
					projectName={project.name}
					projectCup={project.cup}
					isProjectWp={isProjectWp}
					hasEnabledAi={project.aiEnabled}
					key="timesheets-key"
					hasExternalJobTool={project.advancedConfig?.some((el) => el.name === 'enable_external_tools' && el.enabled)}
					refresh={refresh}
					setRefresh={setRefresh}
				/>
			),
		},
		{
			eventKey: 'industrialaccounts',
			title: 'Contabilità Industriale',
			transCode: 'project.execution.industrial_accounting',
			shouldBeVisible: project && project.streamConfigurationType && project.streamConfigurationType !== 'WP',
			content: <IndustrialAccounts projectId={projectId} />,
		},
	];

	const templatesToExport = tabNavItems
		.filter((tabNavItem) => tabNavItem.shouldBeVisible)
		.filter((tabNavItem) => tabNavItem.eventKey !== 'industrialaccounts')
		.map((tabNavItem) => ({
			label: tabNavItem.title,
			value: tabNavItem.eventKey,
		}));

	return (
		<>
			{isOe || userAccess.isExternalAdmin || (isPartnerUser && isProjectWp) ? (
				<div
					style={{
						paddingBlock: '1rem',
						paddingInline: '1.5rem',
						border: '1px solid var(--color-border-secondary-light, #C7CFD7)',
						borderRadius: '12px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginTop: '3rem',
						marginBottom: '3rem',
					}}
				>
					<p className="first-letter-uppercase" style={{ marginBottom: 0 }}>
						{canGenerateTemplates ? 'configura e ' : null}scarica il template e poi carica configurazione checklist e documenti.
					</p>
					<div
						style={{
							display: 'flex',
							gap: '1.5rem',
						}}
					>
						{canGenerateTemplates ? (
							<>
								<Button
									onClick={() => {
										setTemplateModalOpen(true);
									}}
									className="btn custom-outline-button"
									style={{
										display: 'flex',
										gap: '0.5rem',
										fontWeight: 500,
										alignItems: 'center',
										height: '48px',
										padding: '12px 24px',
									}}
								>
									<ConfigureTemplate />
									<span>Configura template</span>
								</Button>
								{isTemplateModalOpen && (
									<GenerateTrackRecord
										entityName={project.name}
										entityId={projectId}
										cancelFn={() => setTemplateModalOpen(false)}
										showModalTrackRecord={isTemplateModalOpen}
										entityType="projects"
										infratelSettings={project?.company?.infratelDomain}
									/>
								)}
							</>
						) : null}

						<ExportTemplateModal templatesToExport={templatesToExport} />
					</div>
				</div>
			) : null}

			<NavTabs tabNavItems={tabNavItems} />
		</>
	);
};
export default Execution;
